import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Snackbar
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { UrlApi } from '../../services/apirest';
import Navbar from '../Navbar';
import { useNotificaciones } from '../../Hooks/useNotificaciones';

const CanjeRegistro = () => {
    const [empresas, setEmpresas] = useState([]);
    const [tiposEnvase, setTiposEnvase] = useState([]);
    const [stockData, setStockData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState('');

    // Obtener datos del localStorage
    const sucursalId = localStorage.getItem('sucursal');
    const username = localStorage.getItem('user');
    const sucursalnombre = localStorage.getItem('sucursalnombre');

    const fechaHoy = new Date().toISOString().split('T')[0];

    const { enviarNotificacion } = useNotificaciones()

    // Cargar datos iniciales
    useEffect(() => {
        if (!sucursalId || !username) {
            setError('No se encontró información de sucursal o usuario en el sistema');
            setLoading(false);
            return;
        }

        cargarDatos();
    }, [sucursalId]);

    const cargarDatos = async () => {
        try {
            setLoading(true);

            // Cargar empresas, tipos de envase y stock actual en paralelo
            const [empresasRes, tiposRes, stockRes] = await Promise.all([
                axios.get(UrlApi + '/canjes_routes/canje_empresas'),
                axios.get(UrlApi + '/canjes_routes/canje_tipos_envase'),
                axios.get(`${UrlApi}/canjes_routes/canje_stock/hoy/${sucursalId}`)
            ]);

            setEmpresas(empresasRes.data);
            setTiposEnvase(tiposRes.data);

            // Convertir el stock en un objeto clave-valor para fácil acceso
            const stockMap = {};
            stockRes.data.stock.forEach(item => {
                const key = `${item.empresa_id}-${item.tipo_envase_id}`;
                stockMap[key] = item.cantidad;
            });
            setStockData(stockMap);

            setError(null);
        } catch (err) {
            console.error('Error al cargar datos:', err);
            setError('Error al cargar los datos. Por favor, intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleCantidadChange = (empresaId, tipoEnvaseId, valor) => {
        // Validar que sea entero no negativo
        const valorNumerico = parseInt(valor) || 0;
        if (valorNumerico < 0) return;

        const key = `${empresaId}-${tipoEnvaseId}`;
        setStockData(prev => ({
            ...prev,
            [key]: valorNumerico
        }));
    };

    const handleGuardar = async () => {
        if (!sucursalId || !username) {
            setError('No se encontró información de sucursal o usuario');
            return;
        }

        try {
            setSaving(true);

            // Crear array de promesas para guardar cada registro
            const promesas = [];

            empresas.forEach(empresa => {
                tiposEnvase.forEach(tipo => {
                    const key = `${empresa.id}-${tipo.id}`;
                    const cantidad = stockData[key] || 0;

                    promesas.push(
                        axios.post(UrlApi + '/canjes_routes/canje_stock', {
                            empresa_id: empresa.id,
                            tipo_envase_id: tipo.id,
                            sucursal_id: sucursalId,
                            cantidad: cantidad,
                            username: username
                        })
                    );
                });
            });

            await Promise.all(promesas);

            setSuccessMsg('Stock guardado correctamente');
            setError(null);

            //await enviarNotificacion('eduardo','✅ Aprobado','El pedido #123 fue aprobado' );

        } catch (err) {
            console.error('Error al guardar:', err);
            setError('Error al guardar el stock. Por favor, intente nuevamente.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Navbar />
            <Box sx={{ p: 3 }}>
                <Paper sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h5" component="h2">
                            Canjes - Registro de Envases - {fechaHoy} - ( {sucursalId} - {sucursalnombre} )
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={handleGuardar}
                            disabled={saving}
                        >
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                        <Table
                            size="small"
                            stickyHeader
                            sx={{
                                '& .MuiTableCell-root': {
                                    padding: '6px 8px'
                                }
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            fontSize: '0.875rem',
                                            borderBottom: '2px solid #ddd'
                                        }}
                                    >
                                        Empresa
                                    </TableCell>
                                    {tiposEnvase.map(tipo => (
                                        <TableCell
                                            key={tipo.id}
                                            align="center"
                                            sx={{
                                                fontWeight: 'bold',
                                                backgroundColor: '#f5f5f5',
                                                fontSize: '0.875rem',
                                                borderBottom: '2px solid #ddd'
                                            }}
                                        >
                                            {tipo.capacidad_kg}kg
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {empresas.map(empresa => (
                                    <TableRow key={empresa.id} hover>
                                        <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                            {empresa.nombre_empresa}
                                        </TableCell>
                                        {tiposEnvase.map(tipo => {
                                            const key = `${empresa.id}-${tipo.id}`;
                                            const valor = stockData[key] || 0;

                                            return (
                                                <TableCell key={tipo.id} align="center">
                                                    <TextField
                                                        type="number"
                                                        value={valor}
                                                        onChange={(e) => handleCantidadChange(empresa.id, tipo.id, e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        size="small"
                                                        inputProps={{
                                                            min: 0,
                                                            step: 1,
                                                            style: {
                                                                textAlign: 'center',
                                                                padding: '4px 6px',
                                                                fontSize: '0.875rem'
                                                            }
                                                        }}
                                                        sx={{
                                                            width: '70px',
                                                            '& .MuiInputBase-root': {
                                                                height: '32px'
                                                            },
                                                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                                                display: 'none',
                                                            },
                                                            '& input[type=number]': {
                                                                MozAppearance: 'textfield',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Snackbar
                    open={!!successMsg}
                    autoHideDuration={3000}
                    onClose={() => setSuccessMsg('')}
                    message={successMsg}
                />
            </Box>
        </>
    );
};

export default CanjeRegistro;